import React, { useState, useEffect, useRef } from 'react';

/**
 * Animates a numeric value from 0 to `target` when it scrolls into view.
 * `target` should be a number. `prefix`/`suffix` are decorative strings.
 */
function useCountUp(target, duration = 1800, startOnView = true) {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // Start when element enters viewport
  useEffect(() => {
    if (!startOnView) { setStarted(true); return; }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [startOnView]);

  // Animate count
  useEffect(() => {
    if (!started || target === 0) return;
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
      else setValue(target);
    };
    requestAnimationFrame(tick);
  }, [started, target, duration]);

  return { value, ref };
}

/**
 * Single animated stat block.
 *
 * Props:
 *   target   — numeric end value (e.g. 96)
 *   prefix   — shown before number (e.g. '★ ')
 *   suffix   — shown after number (e.g. '+' or ' yrs')
 *   label    — caption below the number
 *   style    — container style overrides
 *   numStyle — number style overrides
 *   labStyle — label style overrides
 *   static   — if true, skip animation and show final value immediately (for non-numeric like '5.0')
 *   display  — override the displayed value entirely (e.g. '★ 5.0' as a static string)
 */
export default function StatCounter({
  target = 0,
  prefix = '',
  suffix = '',
  label = '',
  style = {},
  numStyle = {},
  labStyle = {},
  duration = 1800,
  staticVal = null,
}) {
  const { value, ref } = useCountUp(staticVal !== null ? 0 : target, duration);

  const displayed = staticVal !== null
    ? staticVal
    : `${prefix}${value}${suffix}`;

  return (
    <div ref={ref} style={{ textAlign: 'center', ...style }}>
      <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ms-blue)', display: 'block', fontVariantNumeric: 'tabular-nums', ...numStyle }}>
        {displayed}
      </span>
      <span style={{ fontSize: '11px', color: 'var(--text-s)', letterSpacing: '0.04em', ...labStyle }}>
        {label}
      </span>
    </div>
  );
}
